import { Post1Icon } from '@/components/ui/icons/icons-new'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/shadcn/hover-card'
import { CopyIcon, CheckIcon, Loader2 } from 'lucide-react'
import { useState } from 'react'

interface Props {
  imageUrl?: string
  isGenerating?: boolean
  placeholderText?: string
  alt?: string
  enhancedPrompt?: string
}

export function ImagePreviewContainer({
  imageUrl,
  isGenerating = false,
  placeholderText = 'Your generated image will appear here',
  alt = 'Generated',
  enhancedPrompt,
}: Props) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    if (!enhancedPrompt) return
    try {
      await navigator.clipboard.writeText(enhancedPrompt)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard API unavailable (e.g. insecure context)
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="border-border bg-card flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-lg border p-4">
        {isGenerating ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
            <p className="text-sm">Generating your image...</p>
          </div>
        ) : imageUrl ? (
          <img
            src={imageUrl}
            alt={alt}
            className="shadow-xs max-h-full max-w-full rounded-lg border object-contain"
          />
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Post1Icon width={48} height={48} color="var(--foreground)" />
            <p className="text-sm">{placeholderText}</p>
          </div>
        )}
      </div>

      {enhancedPrompt && imageUrl && !isGenerating && (
        <HoverCard openDelay={200}>
          <HoverCardTrigger asChild>
            <div className="bg-card border-border flex cursor-default items-start gap-2 rounded-lg border px-3 py-2">
              <p className="text-muted-foreground line-clamp-2 min-w-0 flex-1 text-xs leading-relaxed">
                {enhancedPrompt}
              </p>
              <button
                onClick={handleCopy}
                className="text-muted-foreground hover:text-foreground mt-0.5 shrink-0 transition-colors"
                title="Copy prompt">
                {copied ? (
                  <CheckIcon className="h-3.5 w-3.5" />
                ) : (
                  <CopyIcon className="h-3.5 w-3.5" />
                )}
              </button>
            </div>
          </HoverCardTrigger>
          <HoverCardContent side="top" className="w-96">
            <p className="text-muted-foreground text-xs leading-relaxed">{enhancedPrompt}</p>
          </HoverCardContent>
        </HoverCard>
      )}
    </div>
  )
}
